import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import type { MastermindSpace, Topic } from "@shared/schema";
import { useAuth } from "@/contexts/AuthContext";
import {
  knowledgeBaseService,
  type KnowledgeBaseItem,
} from "@/services/knowledgeBaseService";
import { spaceSharingService } from "@/services/spaceSharingService";
import { topicsService } from "@/services/topicsService";

const MEMBER_QUERY_OPTIONS = {
  staleTime: 60_000,
  gcTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
} as const;

export interface UseSpaceMemberKnowledgeDocumentsOptions {
  enabled?: boolean;
  /** When false, skip the members query (caller already has members). */
  fetchMembers?: boolean;
}

/**
 * Per-space member documents — same React Query keys as MastermindSpaceDetail.
 */
export function useSpaceMemberKnowledgeDocuments(
  spaceId: string | null | undefined,
  options?: UseSpaceMemberKnowledgeDocumentsOptions
) {
  const { user } = useAuth();
  const enabled = options?.enabled !== false && !!spaceId;
  const fetchMembers = options?.fetchMembers !== false;

  const { data: spaceMembers = [], isLoading: membersLoading } = useQuery({
    queryKey: ["/api/spaces", spaceId, "members"],
    queryFn: async () => {
      if (!spaceId) return [];
      return await spaceSharingService.getSpaceMembers(spaceId);
    },
    enabled: enabled && fetchMembers,
    ...MEMBER_QUERY_OPTIONS,
  });

  const { data: topics = [], isLoading: topicsLoading } = useQuery<Topic[]>({
    queryKey: ["/api/spaces", spaceId, "topics"],
    queryFn: async () => {
      if (!spaceId) return [];
      const hasAccess = await spaceSharingService.hasSpaceAccess(spaceId);
      if (!hasAccess) return [];
      return await topicsService.getTopics(spaceId);
    },
    enabled,
    ...MEMBER_QUERY_OPTIONS,
  });

  const topicIds = useMemo(() => topics.map((t) => t.id), [topics]);
  const topicIdsKey = useMemo(
    () => [...topicIds].sort().join(","),
    [topicIds]
  );

  const otherMemberUserIds = useMemo(() => {
    if (!user?.id || !spaceMembers || spaceMembers.length === 0) return [];
    return spaceMembers
      .map((m) => m.userId)
      .filter((id) => id !== user.id);
  }, [spaceMembers, user?.id]);

  const otherMemberKey = useMemo(
    () => [...otherMemberUserIds].sort().join(","),
    [otherMemberUserIds]
  );

  const {
    data: memberDocuments = [],
    isLoading: docsLoading,
    isFetching: docsFetching,
  } = useQuery<KnowledgeBaseItem[]>({
    queryKey: [
      "/api/knowledgebase-members",
      spaceId,
      topicIdsKey,
      otherMemberKey,
    ],
    queryFn: async () => {
      if (!spaceId || otherMemberUserIds.length === 0) return [];
      return await knowledgeBaseService.getKnowledgeBaseItemsForSpace(
        spaceId,
        topicIds,
        otherMemberUserIds
      );
    },
    enabled: enabled && otherMemberUserIds.length > 0,
    ...MEMBER_QUERY_OPTIONS,
    placeholderData: [],
  });

  return {
    memberDocuments,
    otherMemberUserIds,
    topicIds,
    spaceMembers,
    isLoading:
      membersLoading || topicsLoading || docsLoading || docsFetching,
  };
}

export interface UseAllSpacesMemberKnowledgeDocumentsOptions {
  enabled?: boolean;
}

/**
 * Aggregates member documents across all visible spaces using shared cache keys.
 */
export function useAllSpacesMemberKnowledgeDocuments(
  spaces: MastermindSpace[],
  options?: UseAllSpacesMemberKnowledgeDocumentsOptions
) {
  const { user } = useAuth();
  const enabled = options?.enabled !== false && !!user;
  const spaceIds = useMemo(() => spaces.map((s) => s.id).filter(Boolean), [spaces]);

  const memberQueries = useQueries({
    queries: spaceIds.map((spaceId) => ({
      queryKey: ["/api/spaces", spaceId, "members"],
      queryFn: async () => spaceSharingService.getSpaceMembers(spaceId),
      enabled,
      ...MEMBER_QUERY_OPTIONS,
    })),
  });

  const topicQueries = useQueries({
    queries: spaceIds.map((spaceId) => ({
      queryKey: ["/api/spaces", spaceId, "topics"],
      queryFn: async () => {
        const hasAccess = await spaceSharingService.hasSpaceAccess(spaceId);
        if (!hasAccess) return [] as Topic[];
        return await topicsService.getTopics(spaceId);
      },
      enabled,
      ...MEMBER_QUERY_OPTIONS,
    })),
  });

  const memberDocQueryConfigs = useMemo(() => {
    return spaceIds.map((spaceId, index) => {
      const members = memberQueries[index]?.data ?? [];
      const topics = topicQueries[index]?.data ?? [];
      const topicIds = topics.map((t) => t.id);
      const topicIdsKey = [...topicIds].sort().join(",");
      const otherMemberUserIds =
        user?.id && members.length > 0
          ? members.map((m) => m.userId).filter((id) => id !== user.id)
          : [];
      const otherMemberKey = [...otherMemberUserIds].sort().join(",");
      const membersReady = memberQueries[index]?.isSuccess ?? false;
      const topicsReady = topicQueries[index]?.isSuccess ?? false;

      return {
        spaceId,
        topicIds,
        otherMemberUserIds,
        queryKey: [
          "/api/knowledgebase-members",
          spaceId,
          topicIdsKey,
          otherMemberKey,
        ] as const,
        enabled:
          enabled &&
          membersReady &&
          topicsReady &&
          otherMemberUserIds.length > 0,
      };
    });
  }, [spaceIds, memberQueries, topicQueries, user?.id, enabled]);

  const memberDocQueries = useQueries({
    queries: memberDocQueryConfigs.map((cfg) => ({
      queryKey: cfg.queryKey,
      queryFn: async () => {
        if (!cfg.spaceId || cfg.otherMemberUserIds.length === 0) return [];
        return await knowledgeBaseService.getKnowledgeBaseItemsForSpace(
          cfg.spaceId,
          cfg.topicIds,
          cfg.otherMemberUserIds
        );
      },
      enabled: cfg.enabled,
      ...MEMBER_QUERY_OPTIONS,
      placeholderData: [] as KnowledgeBaseItem[],
    })),
  });

  const documentsBySpaceId = useMemo(() => {
    const map = new Map<string, KnowledgeBaseItem[]>();
    memberDocQueryConfigs.forEach((cfg, index) => {
      const docs = memberDocQueries[index]?.data ?? [];
      if (docs.length > 0) {
        map.set(cfg.spaceId, docs);
      }
    });
    return map;
  }, [memberDocQueryConfigs, memberDocQueries]);

  const memberDocuments = useMemo(() => {
    const seen = new Set<string>();
    const result: KnowledgeBaseItem[] = [];
    for (const docs of documentsBySpaceId.values()) {
      for (const doc of docs) {
        if (!seen.has(doc.id)) {
          seen.add(doc.id);
          result.push(doc);
        }
      }
    }
    return result;
  }, [documentsBySpaceId]);

  const isLoading =
    memberQueries.some((q) => q.isLoading) ||
    topicQueries.some((q) => q.isLoading) ||
    memberDocQueries.some((q) => q.isLoading || q.isFetching);

  return {
    memberDocuments,
    documentsBySpaceId,
    isLoading,
  };
}
